import { z } from 'zod';
import { AnnotationTypeSchema } from 'packages/lib/api/annotations-app-api/contract/annotation-types';
import { CachedDataSchema } from 'packages/lib/api/annotations-app-api/contract/cached-data-type';

export const InsightDimensionSchema = z.object({
  status: z.enum(['EXCELLENT', 'GOOD', 'NEEDS_IMPROVEMENT', 'CRITICAL']),
  score: z.coerce.number().min(0).max(100),
  summary: z.string(),
  details: z.array(z.string()),
});
export type InsightDimension = z.infer<typeof InsightDimensionSchema>;

/**
 * Result shape for the team-wide taxonomy calibration query.
 * Identifies classes that are most frequently corrected across all workflows.
 */
export const TaxonomyCalibrationResultSchema = z.object({
  confusing_class: z.string(),
  error_count: z.coerce.number().int(),
  error_percentage: z.coerce.number(),
});

/**
 * Result shape for the team-wide attribute calibration query.
 * Identifies attributes that are most frequently applied incorrectly across all workflows.
 */
export const AttributeCalibrationResultSchema = z.object({
  ambiguous_attribute: z.string(),
  error_count: z.coerce.number().int(),
});

export const RawInsightsResponseSchema = z.object({
  user_email: z.union([z.string().email(), z.literal('TEAM_AGGREGATE')]),
  workflow: AnnotationTypeSchema,
  period: z.string(),
  dimensions: z.object({
    taxonomy: InsightDimensionSchema,
    attributes: InsightDimensionSchema,
    geometry: InsightDimensionSchema.optional(),
  }),
  taxonomyCalibration: z.array(TaxonomyCalibrationResultSchema),
  attributeCalibration: z.array(AttributeCalibrationResultSchema),
});
export type RawInsightsResponse = z.infer<typeof RawInsightsResponseSchema>;
export const InsightsResponseSchema = CachedDataSchema(
  RawInsightsResponseSchema
);
export type InsightsResponse = z.infer<typeof InsightsResponseSchema>;

export const InsightSeveritySchema = z.enum([
  'LOW',
  'MEDIUM',
  'HIGH',
  'CRITICAL',
]);
export type InsightSeverity = z.infer<typeof InsightSeveritySchema>;

export const InsightCategorySchema = z.enum([
  'QUALITY',
  'DATA_HEALTH',
  'WORKFORCE',
  'COMPLIANCE',
  'PLANNING',
]);
export type InsightCategory = z.infer<typeof InsightCategorySchema>;

export const ActionItemSchema = z.object({
  title: z.string(),
  description: z.string(),
  priority: InsightSeveritySchema,
});
export type ActionItem = z.infer<typeof ActionItemSchema>;

export const InsightSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  category: InsightCategorySchema,
  severity: InsightSeveritySchema,
  metric_value: z.string(), // Human readable (e.g., "12.5%")
  metric_threshold: z.string(), // The baseline breached (e.g., "> 5%")
  action_item: ActionItemSchema,
  metadata: z.record(z.string(), z.string()).optional(), // Extra context (e.g., specific user_email)
});
export type Insight = z.infer<typeof InsightSchema>;

export const InsightApiResponseSchema = z.array(InsightSchema);
export type RawInsightApiResponse = z.infer<typeof InsightApiResponseSchema>;
